/**
 * The shared look of every control.
 *
 * ## Why this is a string and not a component
 *
 * Because the controls are not all the same element. An input, a textarea and
 * the button that opens a select need to *look* identical and behave nothing
 * alike, and a wrapper component that rendered all three would take a `kind`
 * prop and branch - which is the same thing as this, with more indirection and
 * a worse type signature.
 *
 * Sharing the string is what stops them drifting: one place changes the border
 * on focus, and every control in the platform changes with it.
 */

/** Height, padding and type, for anything the size of a text field. */
export const CONTROL_BASE =
  "w-full rounded-[var(--radius-control)] border bg-app px-3 py-2.5 text-sm text-ink " +
  "transition-colors placeholder:text-faint " +
  "disabled:cursor-not-allowed disabled:opacity-50";

/**
 * The border, which is the only part that carries state.
 *
 * Focus is a border change rather than an outline ring: the app is dark and a
 * ring at this radius reads as a halo. Invalid wins over focus, because a field
 * you are correcting is still invalid while you are in it.
 */
export function controlBorder(invalid = false): string {
  return invalid
    ? "border-negative focus:border-negative"
    : "border-line hover:border-line-lit focus:border-line-strong";
}

/** Everything above, for the common case. */
export function control(invalid = false, extra = ""): string {
  return [CONTROL_BASE, controlBorder(invalid), "outline-none", extra]
    .filter(Boolean)
    .join(" ");
}
